import type { ReactNode } from 'react';
import { ArrowLeftIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { NeedHelpLink } from '@components/common/NeedHelpLink';
import SplitBg from '../../assets/images/onboarding/split-bg.png';
import MobileBakedIllustration from '../../assets/images/onboarding/password-illustration-mobile.png';
import Logo from '../../assets/images/logo/logo.svg';

interface OnboardingSplitLayoutProps {
  children: ReactNode;
  onBack?: () => void;
  /** Show the "Your account is protected" trust footer over the illustration. */
  showTrustFooter?: boolean;
  /** Render the YEP.AI logo in the top-left (Figma Demo step 20046:8241). */
  showLogo?: boolean;
  /**
   * Don't render the illustration on top on mobile (desktop right column still
   * shows it). For steps whose mobile Figma puts the illustration between the
   * heading and the CTA (Demo) — the page renders its own inline mobile copy.
   */
  hideIllustrationOnMobile?: boolean;
  /**
   * Custom right-side illustration. When provided, the layout renders a plain
   * white background with the illustration; without it, the baked store-dashboard
   * background (SplitBg) is used.
   */
  illustration?: ReactNode;
}

/**
 * Typeform-style split shell for the Onboarding 2.0 steps (Setup password, Name,
 * Store URL, … — Figma 19991:8520 desktop, 21371:* mobile).
 *
 * Desktop (lg+): form on the left half, illustration/baked bg on the right.
 * Mobile: single column — illustration on top, form below (Figma mobile). The
 * `children` are rendered exactly once and re-ordered with flex `order` so form
 * state / inputs are never duplicated.
 */
export function OnboardingSplitLayout({
  children,
  onBack,
  showTrustFooter = false,
  showLogo = false,
  hideIllustrationOnMobile = false,
  illustration,
}: OnboardingSplitLayoutProps) {
  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-white font-['Inter',sans-serif]">
      {/* Baked store-dashboard background — desktop full-cover only. */}
      {!illustration && (
        <img
          src={SplitBg}
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-0 hidden h-full w-full object-cover lg:block"
        />
      )}

      {/* Logo (top-left, optional). The interleaved-illustration steps (Demo)
          have no logo on mobile (Figma 21371:3540) — desktop only there. */}
      {showLogo && (
        <img
          src={Logo}
          alt="YepAI"
          className={`absolute left-6 top-5 z-20 h-8 w-auto lg:left-[92px] lg:top-6 lg:h-9 ${
            hideIllustrationOnMobile ? 'hidden lg:block' : ''
          }`}
        />
      )}

      {/* Need Help (top-right). Mobile: Figma 21371:* puts it at y≈68px, 12px.
          [2026-09-17] 这里原先是个 `<div>` —— 有文字有图标，就是点不动，这 11 个页面
          （登录、name、ecommerce-platform、store-url、demo、questionnaire、
          welcome-onboard 等）的商家点下去什么都不会发生。换成真链接。 */}
      <NeedHelpLink className="absolute right-6 top-[68px] z-20 text-[12px] text-[#747c80] lg:right-8 lg:top-7 lg:text-[14px]" />

      {/* Back — mobile: same top row as Need Help (y≈68px) when there's no logo
          (Figma 21371:*), below the logo when there is one. Desktop: fixed. */}
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className={`absolute left-6 z-20 flex items-center gap-2 text-[14px] font-medium text-[#101828] transition hover:text-[#6b33ff] lg:left-[100px] lg:top-[120px] lg:text-[16px] ${
            showLogo && !hideIllustrationOnMobile ? 'top-[100px]' : 'top-[68px]'
          }`}
        >
          <ArrowLeftIcon className="h-5 w-5 lg:h-6 lg:w-6" />
          Back
        </button>
      )}

      {/* Trust footer over the illustration (desktop, baked variant only). */}
      {!illustration && showTrustFooter && (
        <div className="absolute bottom-[8%] left-[58%] z-10 hidden max-w-[300px] items-start gap-3 lg:flex">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-[rgba(107,51,255,0.1)]">
            <LockClosedIcon className="h-5 w-5 text-[#6b33ff]" />
          </span>
          <div>
            <p className="text-[14px] font-semibold text-[#2c3437]">Your account is protected</p>
            <p className="mt-0.5 text-[12px] leading-[16px] text-[#747c80]">
              We use industry-standard security to keep your data safe.
            </p>
          </div>
        </div>
      )}

      {/* Content: single column on mobile (illustration on top, form below),
          two columns on desktop (form left, illustration right). */}
      <div className="relative z-10 flex min-h-screen flex-col lg:flex-row">
        {/* Illustration — order-1 (top) on mobile, order-2 (right) on desktop.
            On mobile the illustration is capped to ~34vh so the form stays on
            screen; on desktop it fills the right column. */}
        <div
          className={`order-1 items-center justify-center px-6 pt-[100px] lg:order-2 lg:flex lg:flex-1 lg:px-8 lg:pt-0 ${
            hideIllustrationOnMobile ? 'hidden' : 'flex'
          }`}
        >
          {illustration ? (
            // Mobile: illustration fills the ~320px column width (Figma 320×360),
            // sized by its own aspect. Desktop: fills the right column.
            <div className="mx-auto flex w-full max-w-[320px] items-center justify-center [&>img]:w-full lg:max-w-[760px]">
              {illustration}
            </div>
          ) : (
            // Baked variant on mobile (Figma 21371:1864/2009): the mobile store-
            // dashboard scene (its left half is empty, so crop to the scene) with
            // the "account is protected" bar at the bottom. Desktop uses the
            // full-cover background + the absolute trust footer instead.
            <div className="mx-auto w-full max-w-[345px] lg:hidden">
              <img
                src={MobileBakedIllustration}
                alt=""
                aria-hidden
                className="h-[380px] w-full rounded-t-[16px] object-cover object-[62%_top]"
              />
              {showTrustFooter && (
                <div className="flex items-center gap-3 rounded-b-[16px] bg-white px-4 py-3 shadow-[0px_20px_25px_rgba(80,70,180,0.08)]">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[rgba(107,51,255,0.1)]">
                    <LockClosedIcon className="h-4 w-4 text-[#6b33ff]" />
                  </span>
                  <div>
                    <p className="text-[13px] font-semibold text-[#2c3437]">
                      Your account is protected
                    </p>
                    <p className="text-[11px] leading-[15px] text-[#747c80]">
                      We use industry-standard security to keep your data safe.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Form — order-2 (below) on mobile, order-1 (left) on desktop. When the
            mobile illustration is hidden the form must clear the top chrome
            itself. */}
        <div
          className={`order-2 flex flex-1 items-start px-6 pb-10 lg:order-1 lg:items-center lg:pb-0 lg:pl-[100px] lg:pr-8 lg:pt-0 ${
            hideIllustrationOnMobile ? 'pt-[100px]' : 'pt-6'
          }`}
        >
          <div className="w-full max-w-[500px]">{children}</div>
        </div>
      </div>
    </div>
  );
}
